import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Flex,
  Stack,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";

import ShortlistEmptyState from "./ShortlistEmptyState";
import CandidateListItem from "./CandidateListItem";
import CandidateDetails from "./CandidateDetails";
import {
  candidateTabSuggested,
  candidateTabInterested,
  candidateTabShortlisted,
  candidateTabApproved,
  candidateTabHired,
  candidateTabRejected,
} from "../../constants/candidateFlow";
import { candidateMatchesDeepLinkId } from "../../helpers/utils";

function AgencyShortlistBody({
  moblyzeJob,
  matchingProgress,
  isArchived,
  tabName,
  agencyCandidateTriageSuggestedCandidates,
  agencyCandidateTriageInterestedCandidates,
  agencyCandidateTriageShortlistedCandidates,
  agencyCandidateTriageApprovedCandidates,
  agencyCandidateTriageHiredCandidates,
  agencyCandidateTriageRejectedCandidates,
  refetch,
}) {
  const [displayList, emptyMessage] = useMemo(() => {
    switch (tabName) {
      case candidateTabSuggested:
        return [agencyCandidateTriageSuggestedCandidates, "No suggested candidates."];
      case candidateTabInterested:
        return [agencyCandidateTriageInterestedCandidates, "No candidates have indicated interest yet."];
      case candidateTabShortlisted:
        return [
          agencyCandidateTriageShortlistedCandidates,
          `You haven't added any candidates onto the shortlist.

          Add candidates and accept interested candidates to build your shortlist.`
        ];
      case candidateTabApproved:
        return [agencyCandidateTriageApprovedCandidates, "The client hasn't approved any candidates yet."];
      case candidateTabHired:
        return [agencyCandidateTriageHiredCandidates, "No candidates have been hired yet."];
      case candidateTabRejected:
        return [agencyCandidateTriageRejectedCandidates, "No candidates have been rejected."];
      default:
        return [[], ""];
    }
  }, [
    tabName,
    agencyCandidateTriageSuggestedCandidates,
    agencyCandidateTriageInterestedCandidates,
    agencyCandidateTriageShortlistedCandidates,
    agencyCandidateTriageApprovedCandidates,
    agencyCandidateTriageHiredCandidates,
    agencyCandidateTriageRejectedCandidates,
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const showCandidateId = searchParams.get("showCandidate");

  const [candidate, setCandidate] = useState(() => {
    if (showCandidateId) {
      const fromUrl = displayList?.find((cr) =>
        candidateMatchesDeepLinkId(cr?.candidate, showCandidateId)
      );
      if (fromUrl?.candidate) {
        return fromUrl.candidate;
      }
    }
    return displayList?.length > 0 ? displayList[0]?.candidate : null;
  });

  const handleSetCandidate = useCallback(
    (newCandidate) => {
      setCandidate(newCandidate);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newCandidate?.candidateProfileId) {
            next.set("showCandidate", newCandidate.candidateProfileId);
          } else {
            next.delete("showCandidate");
          }
          next.delete("tab");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // When the funnel tab auto-switches to match a `showCandidate` URL param,
  // reselect the candidate from the new list so the right person is shown.
  const selectedCandidateMatchesUrl = candidateMatchesDeepLinkId(
    candidate,
    showCandidateId
  );
  useEffect(() => {
    if (!showCandidateId || !displayList?.length) {
      return;
    }
    if (selectedCandidateMatchesUrl) {
      return;
    }
    const fromUrl = displayList.find((cr) =>
      candidateMatchesDeepLinkId(cr?.candidate, showCandidateId)
    );
    if (fromUrl?.candidate) {
      setCandidate(fromUrl.candidate);
    }
  }, [showCandidateId, displayList, selectedCandidateMatchesUrl]);

  // While the backend is still matching candidates for the suggested tab, an
  // empty list isn't really "no candidates" — defer to CandidateListItem so
  // its in-progress progress bar shows instead of the empty state.
  const isMatching =
    typeof matchingProgress === "number" && matchingProgress < 1;
  const isSuggestedMatching =
    tabName === candidateTabSuggested && isMatching;

  if (displayList.length === 0 && !isSuggestedMatching) {
    return (
      <Stack spacing="3">
        <Flex gap="50px" alignItems={"center"} justifyContent={"center"}>
          <ShortlistEmptyState message={emptyMessage} />
        </Flex>
      </Stack>
    );
  }

  const candidateReport = displayList.find(
    (candidateReport) =>
      candidateReport?.candidate?.candidateProfileId === candidate?.candidateProfileId
  );

  let markViewedByRecruiterType = "";

  if (
    tabName === candidateTabInterested
  ) {
    markViewedByRecruiterType = "Interested";
  } else if (tabName === candidateTabApproved) {
    markViewedByRecruiterType = "ApprovedByClient";
  }

  return (
    <Flex gap="24px" h="100%" flex="1" justifyContent={"center"} minHeight="0">
      <CandidateListItem
        setSelect={handleSetCandidate}
        select={candidate}
        showFavourite={tabName === candidateTabSuggested}
        showRejectedLabel={tabName === candidateTabRejected}
        candidateReportList={displayList}
        markViewedByRecruiterType={markViewedByRecruiterType}
        refetch={refetch}
        candidateTriageList={[]}
        criteriaCategories={moblyzeJob?.criteriaCategories || []}
        moblyzeJobId={moblyzeJob?.id}
        matchingProgress={matchingProgress}
        showCandidateId={showCandidateId}
        showAvailabilityFilter
        jobStartDate={moblyzeJob?.startDate}
      />
      {candidate ? (
        <CandidateDetails
          markViewedByRecruiterType={markViewedByRecruiterType}
          refetch={refetch}
          moblyzeJob={moblyzeJob}
          candidateReport={candidateReport}
          showAgencyPassAddActions={
            !isArchived &&
            tabName === candidateTabInterested
          }
          showAgencyRemoveShortlistAction={
            !isArchived &&
            tabName === candidateTabShortlisted
          }
          showAgencyMarkHiredAction={!isArchived && tabName === candidateTabApproved}
          showAgencyAddToShortlistAction={
            !isArchived &&
            tabName === candidateTabRejected
          }
          showClientNotes={tabName === candidateTabApproved}
          showAgencyInviteAction={
            !isArchived &&
            tabName === candidateTabSuggested
          }
          showAgencyFavouriteAction={
            !isArchived &&
            tabName === candidateTabSuggested
          }
          showAgencyRecommendAction={
            !isArchived &&
            tabName === candidateTabSuggested
          }
          showChangeStatusMenu={
            !isArchived &&
            tabName === candidateTabSuggested
          }
          showAIScreeningChat={
            !isArchived &&
            (tabName === candidateTabShortlisted ||
              tabName === candidateTabApproved ||
              tabName === candidateTabHired ||
              tabName === candidateTabRejected)
          }
          showRejectedLabel={tabName === candidateTabRejected}
        />
      ) : (
        <Flex flex="1" alignItems="center" justifyContent="center">
          {displayList.length === 0 && !isSuggestedMatching && (
            <ShortlistEmptyState message={emptyMessage} />
          )}
        </Flex>
      )}
    </Flex>
  );
}

export default function AgencyShortlist({
  moblyzeJob,
  matchingProgress,
  isArchived,
  selectedTab,
  agencyCandidateTriageSuggestedCandidates,
  agencyCandidateTriageInterestedCandidates,
  agencyCandidateTriageShortlistedCandidates,
  agencyCandidateTriageApprovedCandidates,
  agencyCandidateTriageHiredCandidates,
  agencyCandidateTriageRejectedCandidates,
  refetch,
}) {
  if (!moblyzeJob) {
    return null;
  }

  return (
    <>
      <AgencyShortlistBody
        moblyzeJob={moblyzeJob}
        matchingProgress={matchingProgress}
        tabName={selectedTab}
        agencyCandidateTriageSuggestedCandidates={
          agencyCandidateTriageSuggestedCandidates
        }
        agencyCandidateTriageInterestedCandidates={
          agencyCandidateTriageInterestedCandidates
        }
        agencyCandidateTriageShortlistedCandidates={
          agencyCandidateTriageShortlistedCandidates
        }
        agencyCandidateTriageApprovedCandidates={
          agencyCandidateTriageApprovedCandidates
        }
        agencyCandidateTriageHiredCandidates={
          agencyCandidateTriageHiredCandidates
        }
        agencyCandidateTriageRejectedCandidates={
          agencyCandidateTriageRejectedCandidates
        }
        isArchived={isArchived}
        refetch={refetch}
      />
    </>
  );
}
